import React, { useEffect, useState } from 'react';
import { DatePicker, Select, Card, Button } from 'antd';
import { SearchOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { type PredictionsFilters } from '../../../../types/predictions';
import { createApiGateway } from '../../../../apiGateway';
import { useIsMobile } from '../../../../hooks/useIsMobile';

const apiGateway = createApiGateway();

const shortcutHint = ' (⌘/Ctrl+Enter)';

type BidderOption = {
  id: number;
  name: string;
};

interface PredictionsFiltersProps {
  filters: PredictionsFilters;
  onFiltersChange: (filters: Partial<PredictionsFilters>) => void;
  onApplyFilters: () => void;
  loading: boolean;
  hasUnappliedChanges: boolean;
  onExportCsv?: () => void;
  exportDisabled?: boolean;
}

const groupByOptions = [
  { label: 'SSP', value: 'sspId' },
  { label: 'DSP', value: 'dspId' },
];

const formatOptions = [
  { label: 'Summary', value: 'summary' },
  { label: 'By Day', value: 'by_day' },
  { label: 'By Hour', value: 'by_hour' },
];

export const PredictionsFiltersComponent: React.FC<PredictionsFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  loading,
  hasUnappliedChanges,
  onExportCsv,
  exportDisabled,
}) => {
  const isMobile = useIsMobile();
  const [ssps, setSsps] = useState<BidderOption[]>([]);
  const [dsps, setDsps] = useState<BidderOption[]>([]);

  useEffect(() => {
    (async () => {
      const [sspsResponse, dspsResponse] = await Promise.all([
        apiGateway.ssps.getAll({ includeSpend: false }),
        apiGateway.dsps.getAll({ includeSpend: false }),
      ]);

      setSsps(sspsResponse);
      setDsps(dspsResponse);
    })();
  }, []);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onFiltersChange({
        dateRange: [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')],
      });
    }
  };

  const handleSelectChange =
    (field: keyof PredictionsFilters) => (value: string | number | (string | number)[] | null) => {
      let processedValue: string | number | (string | number)[] | null = value;

      if (field === 'groupBy') {
        processedValue = Array.isArray(value) && value.length > 0 ? value : null;
      } else {
        processedValue = value || null;
      }

      const newFilters: Partial<PredictionsFilters> = {
        [field]: processedValue,
      };

      if (field === 'sspId') {
        newFilters.dspId = null;
      }
      if (field === 'dspId') {
        newFilters.sspId = null;
      }

      onFiltersChange(newFilters);
    };

  const sspOptions = _.sortBy(
    (ssps || []).map((ssp) => ({
      label: `[${ssp.id}] ${ssp.name}`,
      value: ssp.id,
    })),
    'value'
  );

  const dspOptions = _.sortBy(
    (dsps || []).map((dsp) => ({
      label: `[${dsp.id}] ${dsp.name}`,
      value: dsp.id,
    })),
    'value'
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (!loading) {
          e.preventDefault();
          onApplyFilters();
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onApplyFilters, loading]);

  return (
    <Card
      size="small"
      style={{
        marginBottom: '12px',
        borderRadius: '6px',
      }}
      styles={{ body: { padding: '12px' } }}
    >
      <div
        className="xchange-filters"
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            flex: '1',
            minWidth: '0',
          }}
        >
          <DatePicker.RangePicker
            value={[dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]}
            onChange={handleDateRangeChange}
            size="small"
            style={{ minWidth: '200px', flex: '0 0 auto' }}
          />

          <Select
            options={formatOptions}
            value={filters.format}
            onChange={handleSelectChange('format')}
            placeholder="Format"
            size="small"
            style={{ minWidth: '100px', width: '120px' }}
          />

          <Select
            mode="multiple"
            options={groupByOptions}
            value={filters.groupBy}
            onChange={handleSelectChange('groupBy')}
            placeholder="Group By"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            options={sspOptions}
            mode="multiple"
            value={filters.sspId as unknown as number[] | null}
            onChange={handleSelectChange('sspId')}
            placeholder="SSP"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            options={dspOptions}
            mode="multiple"
            value={filters.dspId as unknown as number[] | null}
            onChange={handleSelectChange('dspId')}
            placeholder="DSP"
            size="small"
            style={{ minWidth: '120px', width: '160px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        <div style={{ flexShrink: 0, marginLeft: '8px', display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onExportCsv}
            disabled={loading || exportDisabled}
            title="Export current view to CSV"
            style={{ minWidth: '110px' }}
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            size="small"
            icon={loading ? <LoadingOutlined /> : <SearchOutlined />}
            onClick={onApplyFilters}
            loading={loading}
            title={'Fetch (⌘/Ctrl + Enter)'}
            style={{
              ...(hasUnappliedChanges && {
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
              }),
              minWidth: '120px',
            }}
          >
            {loading
              ? 'Fetching...'
              : `${hasUnappliedChanges ? 'Fetch Predictions' : 'Up to Date'}${isMobile ? '' : shortcutHint}`}
          </Button>
        </div>
      </div>
    </Card>
  );
};
